import { type HTMLAttributes, forwardRef } from 'react';
import { cn } from '@/lib/utils';

type BadgeColor = 'gray' | 'blue' | 'green' | 'yellow' | 'red' | 'purple' | 'pink' | 'orange' | 'cyan';

export interface BadgeProps extends HTMLAttributes<HTMLSpanElement> {
  variant?: 'default' | 'primary' | 'secondary' | 'success' | 'warning' | 'danger' | 'outline' | 'ghost';
  color?: BadgeColor;
  size?: 'xs' | 'sm' | 'md';
  dot?: boolean;
  glow?: boolean;
}

const Badge = forwardRef<HTMLSpanElement, BadgeProps>(
  ({ className, variant = 'default', color, size = 'sm', dot = false, glow = false, children, ...props }, ref) => {
    // Color-based variants (used when `color` prop is provided)
    const colorVariants: Record<BadgeColor, string> = {
      gray: cn(
        'bg-gray-100 text-gray-700',
        'dark:bg-secondary-800 dark:text-secondary-300'
      ),
      blue: cn(
        'bg-primary-100 text-primary-700',
        'dark:bg-primary-500/20 dark:text-primary-300'
      ),
      cyan: cn(
        'bg-cyan-100 text-cyan-700',
        'dark:bg-cyan-500/20 dark:text-cyan-300'
      ),
      green: cn(
        'bg-success-100 text-success-700',
        'dark:bg-success-500/20 dark:text-success-400'
      ),
      yellow: cn(
        'bg-warning-100 text-warning-700',
        'dark:bg-warning-500/20 dark:text-warning-400'
      ),
      red: cn(
        'bg-danger-100 text-danger-700',
        'dark:bg-danger-500/20 dark:text-danger-400'
      ),
      purple: cn(
        'bg-purple-100 text-purple-700',
        'dark:bg-purple-500/20 dark:text-purple-400'
      ),
      pink: cn(
        'bg-pink-100 text-pink-700',
        'dark:bg-pink-500/20 dark:text-pink-400'
      ),
      orange: cn(
        'bg-orange-100 text-orange-700',
        'dark:bg-orange-500/20 dark:text-orange-400'
      ),
    };

    // Legacy variant-based styles
    const variants = {
      default: cn(
        'bg-gray-100 text-gray-700',
        'dark:bg-secondary-800 dark:text-secondary-300'
      ),
      primary: cn(
        'bg-primary-100 text-primary-700',
        'dark:bg-primary-500/20 dark:text-primary-300'
      ),
      secondary: cn(
        'bg-gray-100 text-gray-600',
        'dark:bg-secondary-800/60 dark:text-secondary-400'
      ),
      success: cn(
        'bg-success-100 text-success-700',
        'dark:bg-success-500/20 dark:text-success-400'
      ),
      warning: cn(
        'bg-warning-100 text-warning-700',
        'dark:bg-warning-500/20 dark:text-warning-400'
      ),
      danger: cn(
        'bg-danger-100 text-danger-700',
        'dark:bg-danger-500/20 dark:text-danger-400'
      ),
      outline: cn(
        'bg-transparent border border-gray-300 text-gray-700',
        'dark:border-white/[0.15] dark:text-secondary-300'
      ),
      ghost: cn(
        'bg-transparent text-gray-600',
        'dark:text-secondary-400'
      ),
    };

    const sizes = {
      xs: 'px-1.5 py-0.5 text-2xs',
      sm: 'px-2 py-0.5 text-xs',
      md: 'px-2.5 py-1 text-xs',
    };

    const dotColors: Record<BadgeColor, string> = {
      gray: 'bg-gray-500 dark:bg-secondary-400',
      blue: 'bg-primary-500',
      cyan: 'bg-cyan-500',
      green: 'bg-success-500',
      yellow: 'bg-warning-500',
      red: 'bg-danger-500',
      purple: 'bg-purple-500',
      pink: 'bg-pink-500',
      orange: 'bg-orange-500',
    };

    const legacyDotColors = {
      default: 'bg-gray-500 dark:bg-secondary-400',
      primary: 'bg-primary-500',
      secondary: 'bg-gray-400 dark:bg-secondary-500',
      success: 'bg-success-500',
      warning: 'bg-warning-500',
      danger: 'bg-danger-500',
      outline: 'bg-gray-500 dark:bg-secondary-400',
      ghost: 'bg-gray-500 dark:bg-secondary-400',
    };

    const glowColors: Record<BadgeColor, string> = {
      gray: '',
      blue: 'shadow-[0_0_8px_rgba(59,130,246,0.4)]',
      cyan: 'shadow-[0_0_8px_rgba(6,182,212,0.4)]',
      green: 'shadow-[0_0_8px_rgba(16,185,129,0.4)]',
      yellow: 'shadow-[0_0_8px_rgba(245,158,11,0.4)]',
      red: 'shadow-[0_0_8px_rgba(239,68,68,0.4)]',
      purple: 'shadow-[0_0_8px_rgba(168,85,247,0.4)]',
      pink: 'shadow-[0_0_8px_rgba(236,72,153,0.4)]',
      orange: 'shadow-[0_0_8px_rgba(249,115,22,0.4)]',
    };

    // Determine which styling to use
    const badgeStyles = color ? colorVariants[color] : variants[variant];
    const dotStyle = color ? dotColors[color] : legacyDotColors[variant];
    const glowStyle = glow && color ? glowColors[color] : '';

    return (
      <span
        ref={ref}
        className={cn(
          'inline-flex items-center gap-1 rounded-md font-medium',
          badgeStyles,
          sizes[size],
          glow && 'dark:' + glowStyle,
          className
        )}
        {...props}
      >
        {dot && <span className={cn('h-1.5 w-1.5 rounded-full', dotStyle)} />}
        {children}
      </span>
    );
  }
);

Badge.displayName = 'Badge';

export { Badge };
